import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import OvVideoReplacementNote from '../molecules/OvVideoReplacementNote';
import OvVideoReplacesNote from '../molecules/OvVideoReplacesNote';
import OvVideoProcessingRetry from '../molecules/OvVideoProcessingRetry';
import OvVideoStateNotice from '../molecules/OvVideoStateNotice';
import { AdminVideo } from '../../../common/model/dto/video/admin-video';
import { VideoUploadItem } from '../../../context/video-upload.types';
import { getVideoLifecycleNotices } from '../../../common/utils/video-notice-utils';
import { isArchived } from '../../../common/utils/video-status-utils';
import { usePendingReplacement } from '../../../hooks/use-pending-replacement';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvVideoDetailsNoticesProps {
  video: AdminVideo;
  upload?: VideoUploadItem;
  canUpdate: boolean;
  canDelete: boolean;
  isReadOnly: boolean;
  hasRefreshError: boolean;
}

const OvVideoDetailsNotices: FC<OvVideoDetailsNoticesProps> = ({
  video,
  upload,
  canUpdate,
  canDelete,
  isReadOnly,
  hasRefreshError,
}) => {
  const { t } = useTranslation();
  const archived = isArchived(video);
  const canRetry = canUpdate && !archived;
  const notices = getVideoLifecycleNotices(video, upload, canDelete);
  const { isPending, replaced, takesOver } = usePendingReplacement(video);

  return (
    <Notices>
      {hasRefreshError && (
        <Refresh role="alert">{t('videos.details.refreshError')}</Refresh>
      )}
      {isReadOnly && <ReadOnly>{t('videos.details.readOnly')}</ReadOnly>}
      {notices.map((notice) => (
        <OvVideoStateNotice key={notice.kind} notice={notice}>
          {notice.kind === 'transcodeFailed' && canRetry && (
            <OvVideoProcessingRetry videoId={video.id} />
          )}
        </OvVideoStateNotice>
      ))}
      {video.replaced_by_video_id && (
        <OvVideoReplacementNote
          replacementId={video.replaced_by_video_id}
          variant="full"
        />
      )}
      {isPending && video.replaces_video_id && (
        <OvVideoReplacesNote
          replacedId={video.replaces_video_id}
          replaced={replaced}
          takesOver={takesOver}
        />
      )}
    </Notices>
  );
};

export default OvVideoDetailsNotices;

const Notices = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Variables.spacing.SMALL};
  padding: ${Variables.spacing.MEDIUM} ${Variables.spacing.XLARGE};
  border-bottom: 1px solid ${Colours.OV_BORDER_COLOR_V3};

  &:empty {
    display: none;
  }
`;

const Refresh = styled.p`
  margin: 0;
  color: ${Colours.OV_ERROR_ACCESSIBLE};
  font-weight: bold;
`;

const ReadOnly = styled.p`
  margin: 0;
  color: ${Colours.OV_CHARCOAL};
`;
